import * as yup from "yup";

const normalizeAvs = (value) => (value ? value.replace(/[.\s-]/g, "") : value);

const isValidAvsChecksum = (avs) => {
    if (!/^756\d{10}$/.test(avs)) return false;

    const digits = avs.split("").map(Number);
    const checksum = digits.slice(0, 12).reduce((sum, digit, index) => {
        return sum + digit * (index % 2 === 0 ? 1 : 3);
    }, 0);

    const checkDigit = (10 - (checksum % 10)) % 10;

    return checkDigit === digits[12];
};

/**
 * this SCHEMA validate first step of general informations.
 */
export const SCHEMA_SECOND_STEP = yup.object({
    poste: yup.string().required(),
    type_contrat: yup
        .object()
        // .min(3, "pages.add_employee.step_info.form.errors.type_contrat.min")
        .required(
            "pages.add_employee.step_info.form.errors.type_contrat.required"
        ),
    managers: yup
        .object()
        // .email("pages.add_employee.step_info.form.errors.manager.email")
        .required("pages.add_employee.step_info.form.errors.manager.required"),
    services: yup
        .object()
        // .min(3, "pages.add_employee.step_info.form.errors.service.min")
        .required("pages.add_employee.step_info.form.errors.service.required"),
    agencies: yup
        .object()
        // .min(3, "pages.add_employee.step_info.form.errors.agencies.min")
        .required("pages.add_employee.step_info.form.errors.agencies.required"),
    policies: yup
        .object()
        // .min(3, "pages.add_employee.step_info.form.errors.agencies.min")
        .required("pages.add_employee.step_info.form.errors.agencies.required"),
    date_arrivee: yup
        .date("pages.add_employee.step_info.form.errors.date_arrivee.date")
        .typeError(
            "pages.add_employee.step_info.form.errors.date_arrivee.typeError"
        )
        .required(
            "pages.add_employee.step_info.form.errors.date_arrivee.required"
        ),
    date_depart: yup
        .date("pages.add_employee.step_info.form.errors.date_depart.date")
        .nullable("null")
        .notRequired("not required")
        .transform((curr, org) => (org !== "" ? curr : null))
        .min(yup.ref("date_arrivee"), "end date can't be before start date")
        .min(
            yup.ref("trial_period"),
            "end date can't be before trial period date"
        )
        .typeError(
            "pages.add_employee.step_info.form.errors.date_depart.typeError"
        ),
    trial_period: yup
        .date("pages.add_employee.step_info.form.errors.date_period.date")
        .nullable("it's null")
        .transform((curr, org) => (org !== "" ? curr : null))
        .min(yup.ref("date_arrivee"), "trial date can't be before start date")
        .typeError(
            "pages.add_employee.step_info.form.errors.date_period.typeError"
        )
        .required(
            "pages.add_employee.step_info.form.errors.date_period.required"
        ),
    job_dcs: yup.string().nullable(),
    seller_dcs: yup.string().nullable(),
    contract_status_id: yup
        .object()
        .required(
            "pages.add_employee.step_info.form.errors.type_contrat.required"
        ),
    hour_count_per_week: yup
        .number()
        .typeError("Ce champ doit être un nombre")
        .min(0, "il doit être positif")
        .required("Ce champ est obligatoire"),
    part_time_percentage: yup
        .number()
        .typeError("Ce champ doit être un nombre")
        .min(0, "il doit être positif")
        .max(100, "il doit être inférieur ou égal à 100")
        .required("Ce champ est obligatoire"),
});
export const SCHEMA_IDENTITY = yup
    .object({
        personal_infos: yup.object({
            avs: yup
                .string()
                .required("Ce champ est obligatoire")
                .transform((value) => normalizeAvs(value))
                .matches(
                    /^756\d{10}$/,
                    "Ce champ doit commencer par 756 et contenir 11 chiffres"
                )
                .test("avs-checksum", "Ce champ est invalide", (value) =>
                    isValidAvsChecksum(value ?? "")
                ),
        }),
    })
    .required();

export const SCHEMA_FACTSHEET = yup
    .object({
        personal_infos: yup.object({
            birth_name: yup.string().nullable().optional(),
            address: yup.object({
                street: yup.string().required("Ce champ est obligatoire"),
                postal_code: yup.string().required("Ce champ est obligatoire"),
                city: yup.string().required("Ce champ est obligatoire"),
                country: yup
                    .string()
                    .nullable()
                    .required("Ce champ est obligatoire"),
            }),
            nationality: yup.string().required("Ce champ est obligatoire"),
            working_permit: yup.string().nullable().optional(),
            birthdate: yup
                .string()
                .matches(
                    /^\d{4}-\d{2}-\d{2}$/,
                    "Format de date invalide (YYYY-MM-DD)"
                )
                .required("Ce champ est obligatoire"),
            place_of_birth: yup.string().required("Ce champ est obligatoire"),
            country_of_birth: yup
                .string()
                .nullable()
                .required("Ce champ est obligatoire"),
            diploma: yup.string().required("Ce champ est obligatoire"),
            health_insurance: yup.string().required("Ce champ est obligatoire"),
            identity_card_type: yup
                .number()
                .typeError("Ce champ est obligatoire")
                .required("Ce champ est obligatoire"),
            identity_card_number: yup
                .string()
                .required("Ce champ est obligatoire"),
            identity_card_expiry_date: yup
                .string()
                .nullable()
                .matches(
                    /^\d{4}-\d{2}-\d{2}$/,
                    "Format de date invalide (YYYY-MM-DD)"
                )
                .required("Ce champ est obligatoire")
                .test(
                    "expiry-in-future",
                    "La date de validité doit être dans le futur",
                    (value) => {
                        if (!value) return false;
                        return value > new Date().toISOString().slice(0, 10);
                    }
                ),
            avs: yup
                .string()
                .nullable()
                .optional()
                .transform((value) => (value ? normalizeAvs(value) : value))
                .test(
                    "avs-optional-checksum",
                    "Le numéro AVS est invalide",
                    (value) => {
                        if (!value) return true;
                        if (!/^756\d{10}$/.test(value)) return false;
                        return isValidAvsChecksum(value);
                    }
                ),
            marital_status: yup
                .number()
                .typeError("Ce champ est obligatoire")
                .required("Ce champ est obligatoire"),
            couple_registration_date: yup
                .string()
                .nullable()
                .transform((curr, orig) => (orig === "" ? null : curr))
                .when("marital_status", {
                    is: (val) => [2, 3, 5].includes(val),
                    then: (schema) =>
                        schema
                            .required("Ce champ est obligatoire")
                            .matches(
                                /^\d{4}-\d{2}-\d{2}$/,
                                "Format de date invalide"
                            ),
                    otherwise: (schema) => schema.nullable().optional(),
                }),
        }),
        partner: yup.object({
            full_name: yup.string().nullable().optional(),
            birthdate: yup
                .string()
                .nullable()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr))
                .matches(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide"),
            marriage_date: yup
                .string()
                .nullable()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr))
                .matches(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide"),
            working_country: yup.string().nullable().optional(),
            work_start_date: yup
                .string()
                .nullable()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr))
                .matches(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide"),
            work_end_date: yup
                .string()
                .nullable()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr))
                .matches(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide")
                .test(
                    "end-after-start",
                    "La date de fin doit être après la date de début",
                    function (value) {
                        const { work_start_date } = this.parent;
                        if (!value || !work_start_date) return true;
                        return value > work_start_date;
                    }
                ),
            working_permit: yup.string().nullable().optional(),
            working_type: yup.number().nullable().optional(),
        }),
        children: yup.array().of(
            yup.object({
                first_name: yup.string().required("Ce champ est obligatoire"),
                last_name: yup.string().required("Ce champ est obligatoire"),
                birthdate: yup
                    .string()
                    .matches(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide")
                    .required("Ce champ est obligatoire"),
                relationship: yup.string().nullable().optional(),
                address: yup.string().nullable().optional(),
                parental_authority: yup.string().nullable().optional(),
            })
        ),
        bank_account: yup.object({
            bank_name: yup.string().required("Ce champ est obligatoire"),
            iban: yup
                .string()
                .required("Ce champ est obligatoire")
                .transform((value) => value?.replace(/\s/g, ""))
                .matches(
                    /^CH\d{19}$/,
                    "L'IBAN doit être un IBAN suisse valide (ex: CH56 0483 5012 3456 7800 9)"
                ),
        }),
        emergency_contacts: yup
            .array()
            .of(
                yup.object({
                    full_name: yup
                        .string()
                        .required("Ce champ est obligatoire"),
                    phone_number: yup
                        .string()
                        .required("Ce champ est obligatoire"),
                    email: yup
                        .string()
                        .nullable()
                        .optional()
                        .email("Adresse email invalide"),
                    relationship: yup
                        .string()
                        .required("Ce champ est obligatoire"),
                })
            )
            .min(1, "Au moins un contact d'urgence est requis"),
    })
    .required();
export const SCHEMA = yup
    .object({
        name: yup
            .string()
            .min(2, "pages.add_employee.step_info.form.errors.name")
            .required("pages.add_employee.step_info.form.errors.name"),
        last_name: yup
            .string()
            .min(2, "pages.add_employee.step_info.form.errors.last_name")
            .required("pages.add_employee.step_info.form.errors.last_name"),
        used_name: yup
            .string()
            .min(2, "pages.add_employee.step_info.form.errors.used_name"),
        // .required("pages.add_employee.step_info.form.errors.used_name"),
        email: yup
            .string()
            .email("pages.add_employee.step_info.form.errors.email")
            .required("Ce champ est obligatoire"),
        email_pro: yup
            .string()
            .email("pages.add_employee.step_info.form.errors.email_pro")
            .optional("Ce champ est obligatoire"),
        telephone: yup
            .string()
            .min(12, "pages.add_employee.step_info.form.errors.telephone")
            .required("Ce champ est obligatoire"),
        avs_number: yup
            .string()
            .required(
                "pages.add_employee.step_info.form.errors.avs_number_required"
            )
            .transform((value) => normalizeAvs(value))
            .matches(
                /^756\d{10}$/,
                "pages.add_employee.step_info.form.errors.avs_number_start_with_numbers"
            )
            .test(
                "avs-checksum",
                "pages.add_employee.step_info.form.errors.avs_number_invalid",
                (value) => isValidAvsChecksum(value ?? "")
            ),
    })
    .required();

/**
 * this is used for validating task on Dialog modal.add task
 */
export const DIALOG_ADD_TASK_RULES = {
    title: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.title.required.message",
        },
        minLength: {
            value: 2,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.title.minLength.message",
        },
    },
    category: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.category.required.message",
        },
    },
    description: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.description.required.message",
        },
        // minLength: {
        // 	value: 20,
        // 	message:
        // 		"pages.add_employee.step_tasks.dialog_add_task_rules.description.minLength.message",
        // },
    },
    due_date: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.due_date.required.message",
        },
        min: {
            value: new Date().toJSON().split("T")[0],
            message: "you cant choose a date in the past",
        },
    },
    assigned_to: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.assigned_to.required.message",
        },
    },
    related_to: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.related_to.required.message",
        },
    },
    who_validate: {
        required: {
            value: true,
            message:
                "pages.add_employee.step_tasks.dialog_add_task_rules.who_validate.required.message",
        },
    },
};
